import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
export const Gallery = () => {
  const {
    t
  } = useLanguage();
  // Mediterranean lifestyle carousel — beaches, sunsets, snorkel/diving
  // NO boats, NO canyon, NO road/car shots
  const slides = [{
    src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    tag: 'Playa Mediterránea',
    caption: 'Aguas cristalinas de la Costa Blanca'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/wszrBbZpP7Y8oapQMcN4rd/sunset.jpg',
    tag: 'Atardecer en Torrevieja',
    caption: 'Sunsets inolvidables sobre el Mediterráneo',
    rotate: -90
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/tGQK5aJkk8Gbw3PHxind56/cala.jpg',
    tag: 'Cala paradisíaca',
    caption: 'Calas escondidas de agua turquesa'
  }, {
    src: 'https://images.unsplash.com/photo-1544551763-77ef2d0cfc6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    tag: 'Buceo',
    caption: 'Explora el fondo marino'
  }, {
    src: 'https://images.unsplash.com/photo-1582967788606-a171c1080cb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    tag: 'Snorkel',
    caption: 'Vida marina al alcance'
  }, {
    src: 'https://images.unsplash.com/photo-1495954484750-af469f2f9be5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    tag: 'Sunset en la playa',
    caption: 'El cielo se enciende sobre la arena'
  }, {
    src: 'https://images.unsplash.com/photo-1605281317010-fe5ffe798166?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    tag: 'Navegando en Torrevieja',
    caption: 'Tu próximo día perfecto en el Mediterráneo'
  }];
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  useEffect(() => {
    const id = setInterval(() => {
      setDirection(1);
      setIndex(p => (p + 1) % slides.length);
    }, 5500);
    return () => clearInterval(id);
  }, [slides.length]);
  const goTo = (i: number) => {
    setDirection(i > index ? 1 : -1);
    setIndex(i);
  };
  const next = () => {
    setDirection(1);
    setIndex(p => (p + 1) % slides.length);
  };
  const prev = () => {
    setDirection(-1);
    setIndex(p => (p - 1 + slides.length) % slides.length);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };
  return <section id="galeria" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white overflow-hidden" data-id="element-278" data-mp="3g3la1jdq3kdc">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-279" data-mp="2sd7simsrl3x0">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-8 sm:mb-12" data-id="element-280" data-mp="2nghe03wlygdp">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-navy mb-4" data-id="element-281" data-mp="xy2bmditlhi3">
            {t('gallery.title')}
          </h2>
          <p className="text-lg text-gray-500 font-medium" data-id="element-282" data-mp="1lpg4ckkvvf3l">
            {t('gallery.subtitle')}
          </p>
          <div className="mt-6 w-20 h-1 bg-turquoise mx-auto rounded-full" data-id="element-283" data-mp="1qmpslwwjdkpu" />
        </motion.div>

        {/* Carousel */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-navy" data-id="element-284" data-mp="1cpt1rvgf438y">
          <div className="relative h-[320px] sm:h-[420px] md:h-[560px] lg:h-[620px] overflow-hidden" data-id="element-285" data-mp="1hkqaf19scalb">
            <AnimatePresence custom={direction} initial={false} mode="wait" data-id="element-286" data-mp="1705jsaudiam">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 280,
                damping: 32
              },
              opacity: {
                duration: 0.4
              }
            }} className="absolute inset-0" data-id="element-287" data-mp="68607vo2bfms">
                {slides[index].rotate ? <div className="absolute inset-0 overflow-hidden" data-id="element-288" data-mp="zhc0byzvcnfa">
                    <img src={slides[index].src} alt={slides[index].tag} loading="lazy" decoding="async" className="absolute left-1/2 top-1/2 max-w-none" style={{
                  width: '100%',
                  height: 'auto',
                  transform: `translate(-50%, -50%) rotate(${slides[index].rotate}deg) scale(2)`
                }} data-id="element-289" data-mp="vefp4tcnrtld" />
                  </div> : <img src={slides[index].src} alt={slides[index].tag} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-290" data-mp="1zp0ku3odykae" />}
                <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" data-id="element-291" data-mp="29gwpxyow3ao4" />

                {/* Caption */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="absolute bottom-6 left-4 right-4 md:bottom-14 md:left-14 md:right-14 text-white max-w-2xl" data-id="element-292" data-mp="2jcq7xxldnwda">
                  <span className="inline-block bg-turquoise text-navy px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2 sm:mb-3" data-id="element-293" data-mp="1rd6fcfmhjw99">
                    {slides[index].tag}
                  </span>
                  <h3 className="text-lg sm:text-2xl md:text-4xl font-bold leading-tight" data-id="element-294" data-mp="2199dlae7vaqx">
                    {slides[index].caption}
                  </h3>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Arrows */}
          <button onClick={prev} aria-label="Previous slide" className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-turquoise hover:text-navy text-white flex items-center justify-center backdrop-blur-md border border-white/20 transition-all" data-id="element-295" data-mp="2oytyvzxuek3d">
            <ChevronLeft className="w-5 h-5" data-id="element-296" data-mp="3cns5sis2gaix" />
          </button>
          <button onClick={next} aria-label="Next slide" className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-turquoise hover:text-navy text-white flex items-center justify-center backdrop-blur-md border border-white/20 transition-all" data-id="element-297" data-mp="3cobr89gxdh1p">
            <ChevronRight className="w-5 h-5" data-id="element-298" data-mp="2twr6ftn1acyu" />
          </button>

          {/* Indicators */}
          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex space-x-2" data-id="element-299" data-mp="2yo0vas0rdc9o">
            {slides.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-10 bg-turquoise' : 'w-2 bg-white/40'}`} data-id="element-300" data-mp="2k0o7ewnhaug4" data-mpi={i} />)}
          </div>
        </div>
      </div>
    </section>;
};